import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogSlugs, ProtectedPaths } from "@/app/vars/Routes";

export const demoCallArticle: BlogArticle = {
  key: BlogArticleKey.DEMO_CALL,
  slug: BlogSlugs.DEMO_CALL,
  categoryId: "01",
  date: "2026-09-02",
  readTime: 8,
  locales: {
    fr: {
      title: "Analyser un appel de démo : ce qu'il faut en ressortir",
      excerpt:
        "Une démo qui s'est bien passée ne dit rien de la suite. Ce qui la prédit, ce sont les questions restées sans réponse et le moment où l'attention est retombée.",
      description:
        "Ce qu'il faut relire après une démo : les questions sans réponse, les objections techniques, et le moment où l'attention est retombée.",
      ctaTitle: "Relis tes démos au lieu de les oublier",
      ctaText:
        "On repère ce qui revient dans tes appels, les objections, les questions sans réponse, les mots employés, et on en fait de quoi corriger ta démo. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une démo se juge presque toujours au ressenti. Le prospect a souri, il a posé des questions, il a dit que c'était exactement ce qu'il cherchait. Trois semaines plus tard, la relance reste sans réponse.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le problème n'est pas que la démo se soit mal passée. C'est qu'une démo qui se passe bien et une démo qui signe se ressemblent énormément sur le moment, et qu'on ne garde presque rien de ce qui les distingue.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Une démo ne se juge pas comme une découverte",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Un appel de découverte cherche une situation, une douleur et son coût. Une démo, elle, se joue sur un terrain différent : la personne en face a déjà admis le problème, elle évalue maintenant si ta solution y répond sans casser ce qu'elle a déjà.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ce déplacement change ce qu'il faut écouter. En découverte, la matière est le récit du prospect. En démo, la matière est sa réaction : ce qu'il coupe, ce sur quoi il revient, ce qu'il demande deux fois.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "En découverte, tu écoutes ce que la personne raconte. En démo, tu observes ce qu'elle fait de ce que tu montres.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Les quatre choses à ressortir d'une démo",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Les questions restées sans réponse.** Celles où tu as dit que tu vérifierais, ou que tu as contournées. Ce sont elles qui reviendront en comité, sans toi pour y répondre.",
            "**Les objections techniques.** L'intégration qui manque, la donnée qui doit rester quelque part, la contrainte de sécurité. Elles ne se négocient pas, elles se traitent ou elles bloquent.",
            "**Le moment où l'attention est retombée.** La partie de la démo où la personne a cessé de réagir. C'est presque toujours la partie que tu montres par habitude et dont elle n'a pas besoin.",
            "**Ce qu'elle a demandé à revoir.** Une deuxième demande sur le même écran est le signal le plus fiable de la démo. C'est là qu'est sa vraie question.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Aucune de ces quatre choses ne tient dans un compte rendu écrit après coup, parce que toutes les quatre concernent des instants qu'on ne remarque pas en les vivant.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Ce qu'on note, et ce qu'il faudrait noter",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Le compte rendu habituel",
            items: [
              "Bonne démo, prospect intéressé",
              "A aimé la partie reporting",
              "Doit en parler à son équipe",
              "Relancer dans deux semaines",
            ],
          },
          good: {
            title: "Ce qui sert vraiment ensuite",
            items: [
              "Deux questions sans réponse sur l'export des données",
              "A fait revenir trois fois sur l'écran de suivi",
              "A décroché pendant toute la partie paramétrage",
              "Contrainte : la donnée doit rester en Europe",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La colonne de gauche est un ressenti, elle ne se compare pas d'une démo à l'autre. La colonne de droite est faite d'éléments vérifiables, et c'est ce qui permet de voir, au bout de dix démos, que la même question revient à chaque fois.",
        },
        {
          type: ArticleBlockType.H2,
          text: "La question qui revient est ta vraie feuille de route",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une objection entendue une fois est une anecdote. La même objection sur huit démos d'affilée est un défaut, soit de ton produit, soit de ta démo, soit de la cible que tu invites.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ce passage de l'anecdote au motif est ce qu'aucune mémoire ne fait toute seule. On se souvient de la dernière démo, jamais des dix dernières, et on corrige donc en fonction du dernier prospect rencontré plutôt qu'en fonction de ce qui revient.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Enregistrer ne suffit pas, il faut consolider",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Beaucoup d'outils transcrivent une démo et en font un résumé. C'est utile pour retrouver un détail, et ça s'arrête là : tu obtiens un résumé par appel, donc autant de documents que de démos, et la question qui revient reste invisible.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ce qui change la donne, c'est la consolidation : une seule vue qui rassemble les objections, les questions et les mots employés à travers toutes tes démos, et qui les classe par fréquence.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Voir ce qui revient dans tes démos",
          text: "L'analyse d'appels de Meidly lit tes enregistrements et en sort les objections, les questions et les formulations récurrentes, consolidées sur l'ensemble de tes appels et non appel par appel.",
          linkLabel: "Découvrir l'analyse d'appels",
          linkPath: ProtectedPaths.CALL_ANALYSIS,
        },
        {
          type: ArticleBlockType.H2,
          text: "Par où commencer",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "Enregistre tes démos, avec l'accord des participants, et garde-les au même endroit.",
            "Après chaque démo, note seulement les quatre points ci-dessus. Cinq minutes suffisent.",
            "Au bout de dix démos, relis l'ensemble et cherche ce qui apparaît au moins trois fois.",
            "Corrige d'abord ce qui revient le plus, pas ce que le dernier prospect a dit.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le gain n'est pas dans la démo que tu viens de faire, il est dans la vingtième. Une démo relue est une démo qui améliore la suivante ; une démo oubliée se rejoue à l'identique, avec les mêmes angles morts.",
        },
      ],
      faq: [
        {
          question: "Faut-il enregistrer une démo pour l'analyser ?",
          answer:
            "Oui, en pratique. Les quatre éléments qui comptent, les questions sans réponse, les objections techniques, la baisse d'attention et les demandes de retour en arrière, se produisent à des instants qu'on ne remarque pas en les vivant. Un enregistrement suppose l'information et l'accord des participants.",
        },
        {
          question: "Quelle différence avec l'analyse d'un appel de découverte ?",
          answer:
            "La découverte cherche la situation, la douleur et son coût, donc la matière est le récit du prospect. La démo se joue sur sa réaction à ce que tu montres : ce qu'il interrompt, ce sur quoi il revient, ce qu'il demande deux fois.",
        },
        {
          question: "À partir de combien de démos les tendances deviennent lisibles ?",
          answer:
            "Une dizaine suffit à faire apparaître les objections récurrentes. En dessous, tu observes des anecdotes ; une objection entendue une seule fois ne justifie pas de modifier ta démo.",
        },
        {
          question: "Un résumé automatique par appel suffit-il ?",
          answer:
            "Pour retrouver un détail, oui. Pour progresser, non : un résumé par démo produit autant de documents que d'appels, et la question qui revient à chaque fois reste invisible tant que rien ne consolide l'ensemble.",
        },
      ],
    },
    en: {
      title: "Analysing a demo call: what to take from it",
      excerpt:
        "A demo that went well tells you nothing about what follows. What does is the questions left unanswered and the moment attention dropped.",
      description:
        "What to review after a product demo: the questions left unanswered, the technical objections, and the moment attention dropped.",
      ctaTitle: "Review your demos instead of forgetting them",
      ctaText:
        "We surface what keeps coming back in your calls, the objections, the unanswered questions, the words people use, and turn it into what you need to fix your demo. No commitment.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A demo is almost always judged on feel. The prospect smiled, asked questions, said it was exactly what they were looking for. Three weeks later the follow-up goes unanswered.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The problem is not that the demo went badly. It is that a demo which goes well and a demo which closes look very much alike in the moment, and almost nothing that separates them is ever kept.",
        },
        {
          type: ArticleBlockType.H2,
          text: "A demo is not judged like a discovery call",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A discovery call looks for a situation, a pain and what it costs. A demo plays on different ground: the person has already admitted the problem and is now assessing whether your answer fits without breaking what they already run.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "That shift changes what you listen for. In discovery, the material is what the prospect tells you. In a demo, the material is how they react: what they cut short, what they come back to, what they ask about twice.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "In discovery you listen to what the person says. In a demo you watch what they do with what you show them.",
        },
        {
          type: ArticleBlockType.H2,
          text: "The four things to take from a demo",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**The questions left unanswered.** The ones where you said you would check, or that you talked around. Those are the ones that come back in the committee, without you there to answer them.",
            "**The technical objections.** The missing integration, the data that has to stay somewhere, the security constraint. They are not negotiated, they are either handled or they block.",
            "**The moment attention dropped.** The part of the demo where the person stopped reacting. It is nearly always the part you show out of habit and that they do not need.",
            "**What they asked to see again.** A second request on the same screen is the most reliable signal of the whole call. That is where their real question sits.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "None of these four survive a write-up done afterwards, because all four are about moments you do not notice while living them.",
        },
        {
          type: ArticleBlockType.H2,
          text: "What gets written down, and what should be",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "The usual write-up",
            items: [
              "Good demo, prospect interested",
              "Liked the reporting part",
              "Needs to talk to their team",
              "Follow up in two weeks",
            ],
          },
          good: {
            title: "What actually serves you next",
            items: [
              "Two unanswered questions about exporting data",
              "Came back to the tracking screen three times",
              "Disengaged through the whole setup section",
              "Constraint: data has to stay in Europe",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The left column is a feeling, and feelings do not compare from one demo to the next. The right column is made of verifiable items, and that is what lets you see, after ten demos, that the same question comes up every time.",
        },
        {
          type: ArticleBlockType.H2,
          text: "The recurring question is your real roadmap",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "An objection heard once is an anecdote. The same objection across eight demos in a row is a defect, either in your product, in your demo, or in who you invite to it.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Moving from anecdote to pattern is what no memory does on its own. You remember the last demo, never the last ten, so you correct for the most recent prospect rather than for what keeps coming back.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Recording is not enough, it has to be consolidated",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Plenty of tools transcribe a demo and summarise it. That helps you find a detail again, and it stops there: you get one summary per call, so as many documents as demos, and the recurring question stays invisible.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "What changes things is consolidation: a single view gathering the objections, the questions and the words used across all your demos, ranked by how often they appear.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "See what keeps coming back in your demos",
          text: "Meidly's call analysis reads your recordings and pulls out the recurring objections, questions and wordings, consolidated across all your calls rather than call by call.",
          linkLabel: "Discover call analysis",
          linkPath: ProtectedPaths.CALL_ANALYSIS,
        },
        {
          type: ArticleBlockType.H2,
          text: "Where to start",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "Record your demos, with the participants' consent, and keep them in one place.",
            "After each demo, write down only the four points above. Five minutes is enough.",
            "After ten demos, read the set and look for what appears at least three times.",
            "Fix what comes back most often first, not what the last prospect said.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The gain is not in the demo you just ran, it is in the twentieth. A demo you review improves the next one; a demo you forget gets replayed identically, with the same blind spots.",
        },
      ],
      faq: [
        {
          question: "Do you need to record a demo to analyse it?",
          answer:
            "In practice, yes. The four things that matter, unanswered questions, technical objections, the drop in attention and requests to go back, happen at moments you do not notice while living them. Recording requires informing the participants and obtaining their consent.",
        },
        {
          question: "How is this different from analysing a discovery call?",
          answer:
            "Discovery looks for the situation, the pain and its cost, so the material is what the prospect tells you. A demo plays on their reaction to what you show: what they interrupt, what they return to, what they ask about twice.",
        },
        {
          question: "How many demos before patterns become readable?",
          answer:
            "About ten is enough for recurring objections to appear. Below that you are looking at anecdotes, and an objection heard once does not justify changing your demo.",
        },
        {
          question: "Is an automatic per-call summary enough?",
          answer:
            "To find a detail again, yes. To improve, no: one summary per demo produces as many documents as calls, and the question that comes up every time stays invisible until something consolidates the set.",
        },
      ],
    },
  },
};
